import { produce, type Draft } from 'immer';
import {
  blockToText,
  mkBlock,
  mkChain,
  mkLine,
  mkSet,
  nid,
  normFmt,
  textBlock,
  typeOfTitle,
  type Block,
  type BlockItem,
  type ChainLine,
  type ExLine,
  type ExLookup,
  type Line,
  type SetGroup,
  type WorkoutContent,
} from '@tg/domain';

/* Операции над документом тренировки. Все — чистые функции content → content (immer).
   Адресация по id: блок, строка (в блоке или в сете), сет, часть связки. */

export type Doc = WorkoutContent;
type D = Draft<Doc>;

export interface Loc {
  block: Draft<Block>;
  /** массив, где лежит элемент (items блока или items сета) */
  list: Draft<BlockItem[]> | Draft<Line[]>;
  index: number;
  item: Draft<BlockItem>;
  set?: Draft<SetGroup>;
}

export function locate(d: D, id: string): Loc | null {
  for (const block of d.blocks) {
    for (let i = 0; i < block.items.length; i++) {
      const it = block.items[i];
      if (it.id === id) return { block, list: block.items, index: i, item: it };
      if (it.t === 'set')
        for (let j = 0; j < it.items.length; j++) if (it.items[j].id === id) return { block, list: it.items, index: j, item: it.items[j], set: it };
    }
  }
  return null;
}
export const findBlock = (d: D | Doc, id: string) => d.blocks.find((b) => b.id === id) as Draft<Block> | undefined;

export function findLine(d: Doc, id: string): Line | null {
  for (const b of d.blocks)
    for (const it of b.items) {
      if (it.id === id && it.t !== 'set') return it;
      if (it.t === 'set') for (const y of it.items) if (y.id === id) return y;
    }
  return null;
}

export const edit = (doc: Doc, f: (d: D) => void): Doc => produce(doc, f);

/** Сет без двух участников распускается сам (CON-21) */
function normSets(b: Draft<Block>) {
  b.items = b.items.flatMap((y) => (y.t === 'set' && y.items.length < 2 ? (y.items as Draft<BlockItem>[]) : [y]));
}

/* ─── Блоки ─── */

export const addBlock = (doc: Doc, after?: string, text = false) =>
  edit(doc, (d) => {
    const b = text ? textBlock('') : mkBlock({ items: [mkLine(null, '')] });
    const i = after ? d.blocks.findIndex((x) => x.id === after) + 1 : d.blocks.length;
    d.blocks.splice(i || d.blocks.length, 0, b);
  });

/** Блок текстом с готовым текстом (фото тренировки, AI-4) — в конец дня */
export const addTextBlockWith = (doc: Doc, text: string) =>
  edit(doc, (d) => {
    d.blocks.push(textBlock(text) as Draft<Block>);
  });

export const setBlock = (doc: Doc, id: string, patch: Partial<Block>) =>
  edit(doc, (d) => {
    const b = findBlock(d, id);
    if (!b) return;
    Object.assign(b, patch);
    /* Тип по слову в названии, пока тренер не выбрал сам; формат из названия — в настройку (CON-18) */
    if ('title' in patch) {
      if (!b.kind) b.kind = typeOfTitle(b.title);
      normFmt(b as Block);
    }
  });

export const removeBlock = (doc: Doc, id: string) => edit(doc, (d) => void (d.blocks = d.blocks.filter((b) => b.id !== id)));

export const moveBlock = (doc: Doc, id: string, toIndex: number) =>
  edit(doc, (d) => {
    const i = d.blocks.findIndex((b) => b.id === id);
    if (i < 0) return;
    const [b] = d.blocks.splice(i, 1);
    d.blocks.splice(toIndex > i ? toIndex - 1 : toIndex, 0, b);
  });

export const insertBlocks = (doc: Doc, blocks: Block[], at?: number) =>
  edit(doc, (d) => {
    /* Пустой блок «быстрого старта» заменяется вставкой */
    if (d.blocks.length === 1 && isBlank(d.blocks[0] as Block)) d.blocks = [];
    d.blocks.splice(at ?? d.blocks.length, 0, ...(blocks as Draft<Block>[]));
  });

export const isBlankLine = (y: BlockItem) => y.t === 'ex' && !y.exId && !String(y.raw || '').trim();
export const isBlank = (b: Block) => !b.title && !b.note && !b.kind && (typeof b.text === 'string' ? !b.text.trim() : b.items.every(isBlankLine));

/* ─── Строки ─── */

/** Добавить пустую строку в конец блока (или после строки, или в сет) */
export function addLine(doc: Doc, where: { block: string } | { after: string } | { set: string }): [Doc, string] {
  const line = mkLine(null, '');
  const next = edit(doc, (d) => {
    if ('block' in where) {
      findBlock(d, where.block)?.items.push(line);
    } else if ('after' in where) {
      const l = locate(d, where.after);
      if (l) (l.list as Line[]).splice(l.index + 1, 0, line);
    } else {
      const l = locate(d, where.set);
      if (l && l.item.t === 'set') l.item.items.push(line);
    }
  });
  return [next, line.id];
}

export const setLine = (doc: Doc, id: string, patch: Partial<ExLine> | Partial<ChainLine>) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (l) Object.assign(l.item, patch);
  });

export const removeItem = (doc: Doc, id: string) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (!l) return;
    l.list.splice(l.index, 1);
    normSets(l.block);
  });

/** Выбор упражнения из базы для строки: строка текстом → строка упражнения, параметры сохраняются */
export const pickExercise = (doc: Doc, id: string, exId: string) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (!l || l.item.t !== 'ex') return;
    l.item.exId = exId;
    l.item.raw = null;
    /* Другое упражнение — его видео и демонстрация снова подставляются из базы */
    delete l.item.noVideo;
    delete l.item.noGif;
  });

/** Переписанное руками название упражнения из базы — строка уходит в текст (CON-19, «Правка названия») */
export const lineToText = (doc: Doc, id: string, text: string) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (!l || l.item.t !== 'ex') return;
    const it = l.item;
    const params = [it.scheme, it.load ? loadStr(it.load) : '', it.txt].filter(Boolean).join(' ');
    it.exId = null;
    it.raw = [text, params].filter(Boolean).join(' ');
    delete it.scheme;
    delete it.load;
    delete it.txt;
  });

const loadStr = (l: NonNullable<ExLine['load']>) => (l.kind === 'pct' ? `${l.from}${l.to != null ? '-' + l.to : ''}%` : `${l.from}${l.to != null ? '-' + l.to : ''} ${l.unit}`);

/** Перемещение строки: до/после другой строки, в конец блока, в сет */
export function moveItem(doc: Doc, id: string, target: { before: string } | { after: string } | { block: string } | { into: string }): Doc {
  return edit(doc, (d) => {
    const src = locate(d, id);
    if (!src) return;
    const [it] = src.list.splice(src.index, 1);
    if ('block' in target) {
      findBlock(d, target.block)?.items.push(it as Draft<BlockItem>);
    } else if ('into' in target) {
      /* Строка на середину другой строки — две строки становятся сетом (CON-21) */
      const t = locate(d, target.into);
      if (!t || it.t === 'set') return void src.list.splice(src.index, 0, it as any);
      if (t.item.t === 'set') t.item.items.push(it as Draft<Line>);
      else if (t.set) t.set.items.splice(t.index + 1, 0, it as Draft<Line>);
      else (t.list as Draft<BlockItem>[]).splice(t.index, 1, mkSet([t.item as Line, it as Line]) as Draft<SetGroup>);
    } else {
      const ref = 'before' in target ? target.before : target.after;
      const t = locate(d, ref);
      if (!t) return void src.list.splice(src.index, 0, it as any);
      /* Сет в сет не вкладывается */
      if (it.t === 'set' && t.set) {
        const bi = t.block.items.indexOf(t.set);
        t.block.items.splice(bi + ('after' in target ? 1 : 0), 0, it as any);
      } else (t.list as any[]).splice(t.index + ('after' in target ? 1 : 0), 0, it);
    }
    for (const b of d.blocks) normSets(b);
  });
}

/* ─── Сет ─── */

/** Переключатель «Сет» в пустой строке: строка — первое упражнение группы на 3 круга, вторая пустая следом */
export function lineToSet(doc: Doc, id: string): [Doc, string] {
  const second = mkLine(null, '');
  const next = edit(doc, (d) => {
    const l = locate(d, id);
    if (!l || l.set || l.item.t === 'set') return;
    (l.list as Draft<BlockItem>[]).splice(l.index, 1, mkSet([l.item as Line, second], 3, '') as Draft<SetGroup>);
  });
  return [next, second.id];
}

export const setSet = (doc: Doc, id: string, patch: Partial<Pick<SetGroup, 'rounds' | 'rest'>>) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (l && l.item.t === 'set') Object.assign(l.item, patch);
  });

/** Меню блока: «Разгруппировать в текстовый блок» — упражнения строками текста (название, тип, заметка остаются) */
export const blockToTextBlock = (doc: Doc, id: string, ex: ExLookup) =>
  edit(doc, (d) => {
    const b = d.blocks.find((x) => x.id === id);
    if (!b || typeof b.text === 'string') return;
    b.text = blockToText(b as Block, ex);
    b.items = [];
  });

/** Меню блока: «Очистить блок» — упражнения или текст убираются, сам блок (название, тип, заметка) остаётся */
export const clearBlock = (doc: Doc, id: string) =>
  edit(doc, (d) => {
    const b = d.blocks.find((x) => x.id === id);
    if (!b) return;
    if (typeof b.text === 'string') b.text = '';
    else b.items = [];
  });

/** Разгруппировать — упражнения остаются в блоке; удалить — вместе с упражнениями */
export const ungroup = (doc: Doc, id: string) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (l && l.item.t === 'set') (l.list as Draft<BlockItem>[]).splice(l.index, 1, ...(l.item.items as Draft<BlockItem>[]));
  });

/* ─── Связка ─── */

/** Переключатель «Связка» или «+» после упражнения: строка становится связкой, упражнение — первой частью */
export const lineToChain = (doc: Doc, id: string) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (!l || l.item.t !== 'ex') return;
    const it = l.item;
    const c = mkChain(it.exId || String(it.raw || '').trim() ? [{ id: nid('p'), exId: it.exId, raw: it.exId ? null : it.raw, reps: undefined }] : []);
    c.id = it.id;
    if (it.load) c.load = it.load;
    (l.list as any[]).splice(l.index, 1, c);
  });

export const addPart = (doc: Doc, chainId: string, exId: string | null, raw: string | null) =>
  edit(doc, (d) => {
    const l = locate(d, chainId);
    if (l && l.item.t === 'chain') l.item.parts.push({ id: nid('p'), exId, raw });
  });

export const setPart = (doc: Doc, chainId: string, partId: string, reps: string) =>
  edit(doc, (d) => {
    const l = locate(d, chainId);
    if (l && l.item.t === 'chain') {
      const p = l.item.parts.find((x) => x.id === partId);
      if (p) p.reps = reps || undefined;
    }
  });

/** Крестик у части; связка из одной части — снова обычная строка */
export const removePart = (doc: Doc, chainId: string, partId: string) =>
  edit(doc, (d) => {
    const l = locate(d, chainId);
    if (!l || l.item.t !== 'chain') return;
    const c = l.item;
    c.parts = c.parts.filter((p) => p.id !== partId);
    if (c.parts.length <= 1) {
      const p = c.parts[0];
      const line: ExLine = { t: 'ex', id: c.id, exId: p?.exId ?? null, raw: p ? p.raw ?? null : '', ...(p?.reps ? { scheme: p.reps } : {}), ...(c.load ? { load: c.load } : {}) };
      (l.list as any[]).splice(l.index, 1, line);
    }
  });

/* ─── Разбор текстом (кнопка AI) ─── */

/** Заменить блок текстом на собранные блоки; первый наследует название, тип и заметку исходного */
export const replaceBlock = (doc: Doc, id: string, blocks: Block[]) =>
  edit(doc, (d) => {
    const i = d.blocks.findIndex((b) => b.id === id);
    if (i < 0) return;
    const src = d.blocks[i];
    if (blocks[0]) {
      if (src.title && !blocks[0].title) blocks[0].title = src.title;
      if (src.kind && !blocks[0].kind) blocks[0].kind = src.kind;
      if (src.note) blocks[0].note = src.note;
    }
    d.blocks.splice(i, 1, ...(blocks as Draft<Block>[]));
  });

export const replaceLine = (doc: Doc, id: string, items: BlockItem[]) =>
  edit(doc, (d) => {
    const l = locate(d, id);
    if (!l) return;
    if (l.set) (l.list as any[]).splice(l.index, 1, ...items.flatMap((y) => (y.t === 'set' ? y.items : [y])));
    else (l.list as any[]).splice(l.index, 1, ...items);
  });

/** Вставка многострочного текста в строку: несколько строк ложатся блоком текстом (CON-5) */
export function pasteText(doc: Doc, lineId: string, text: string): Doc {
  const lines = text.split('\n').map((s) => s.trim()).filter(Boolean);
  if (lines.length <= 1) return setLine(doc, lineId, { raw: lines[0] ?? '', exId: null });
  return edit(doc, (d) => {
    const l = locate(d, lineId);
    if (!l) return;
    const bi = d.blocks.indexOf(l.block);
    const blank = isBlankLine(l.item as BlockItem);
    if (blank) l.list.splice(l.index, 1);
    const tb = textBlock(text.replace(/\r/g, '').trim());
    if (l.block.items.length === 0 && !l.block.title && !l.block.note) d.blocks.splice(bi, 1, tb as Draft<Block>);
    else d.blocks.splice(bi + 1, 0, tb as Draft<Block>);
  });
}

/** Слепок для сравнения «изменён ли день»: пустые строки и блоки не считаются (как в прототипе) */
export function signature(doc: Doc | null | undefined): string {
  if (!doc) return '';
  const strip = (b: Block) => ({
    ...b,
    id: '',
    items: b.items.filter((y) => !isBlankLine(y)).map((y) => (y.t === 'set' ? { ...y, items: y.items.filter((x) => !isBlankLine(x)) } : y)),
  });
  return JSON.stringify({ t: doc.title || '', c: !!doc.comp, m: doc.message || '', b: doc.blocks.filter((b) => !isBlank(b)).map(strip) });
}

/** Перед записью: пустые строки ввода и пустые блоки — место, куда печатать, а не содержимое */
export function cleanForSave(doc: Doc): Doc {
  return {
    ...doc,
    blocks: doc.blocks
      .filter((b) => !isBlank(b))
      .map((b) => ({ ...b, items: b.items.filter((y) => !isBlankLine(y)).map((y) => (y.t === 'set' ? { ...y, items: y.items.filter((x) => !isBlankLine(x)) } : y)) })),
  };
}
